import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, Clapperboard, Download, KanbanSquare, ListVideo, Loader2, Plus } from 'lucide-react'

import {
  downloadShotsCsv,
  getCollaborationEligibility,
  getProduction,
  getProductionOverview,
  listDepartments,
  listMembers,
  listScriptDocuments,
  listScriptEpisodes,
  listShots,
  type CollaborationEligibility,
  type OverviewEpisodeProgress,
  type Production,
  type ProductionDepartment,
  type ProductionMember,
  type ScriptDocument,
  type ScriptEpisodeSummary,
} from '@/api/studio-production'
import { CatalogListPageShell } from '@/shared/components/catalog/CatalogListPageShell'
import { PageHeading } from '@/shared/components/PageHeading'
import { PageEmptyState } from '@/shared/components/PageEmptyState'
import { CatalogListPrimaryButton } from '@/shared/components/catalog/CatalogListToolbar'
import { ShotCreateModal } from '@/features/studio/modals/ShotCreateModal'
import { ImagePreviewModal } from '@/features/studio/modals/ImagePreviewModal'
import { EpisodeScenesExpandRows } from '@/features/studio/components/EpisodeScenesExpandRows'
import { AssignWorkItemModal, type AssignWorkItemPreset } from '@/features/studio/modals/AssignWorkItemModal'
import { productionBasePath } from '@/features/studio/lib/production-path'
import { buildShotSceneScopeKey } from '@/features/studio/lib/canvas-scope'
import { resolveCollaborationGate } from '@/features/studio/lib/collaboration-gate'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { useAuth } from '@/hooks'
import {
  formatEpisodeLabel,
  groupEpisodesBySeason,
  usesSeasonSplit,
} from '@/features/studio/script/season'
import {
  PRODUCTION_TABLE_HEAD_CLASS,
  PRODUCTION_TABLE_ROW_CLASS,
} from '@/features/studio/lib/production-shell'
import { cn } from '@/shared/lib/utils'
import { ProtoBtn, ProtoDropdown, ProtoListTable, ProtoPagination, ProtoTabs } from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'
import { useClientPagination } from '@/features/studio/hooks/useClientPagination'

const EPISODE_PAGE_SIZE = 15

export function ShotsPage() {
  const { productionId = '' } = useParams()
  const { t } = useTranslation()
  const { user } = useAuth()
  const base = productionBasePath(productionId)

  const [production, setProduction] = useState<Production | null>(null)
  const [members, setMembers] = useState<ProductionMember[]>([])
  const [departments, setDepartments] = useState<ProductionDepartment[]>([])
  const [eligibility, setEligibility] = useState<CollaborationEligibility | null>(null)
  const [assignOpen, setAssignOpen] = useState(false)
  const [assignPreset, setAssignPreset] = useState<AssignWorkItemPreset | undefined>()

  const [scripts, setScripts] = useState<ScriptDocument[]>([])
  const [scriptId, setScriptId] = useState(0)
  const [episodes, setEpisodes] = useState<ScriptEpisodeSummary[]>([])
  const [episodeTarget, setEpisodeTarget] = useState(1)
  const [episodesPerSeason, setEpisodesPerSeason] = useState(0)
  const [progressByEp, setProgressByEp] = useState<Record<number, OverviewEpisodeProgress>>({})
  const [seasonTab, setSeasonTab] = useState('1')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [expandedEpisodes, setExpandedEpisodes] = useState<Set<number>>(new Set())
  const [previewUrls, setPreviewUrls] = useState<string[]>([])
  const [previewIndex, setPreviewIndex] = useState(0)
  const [previewTitle, setPreviewTitle] = useState('')

  const canManageScenes =
    production?.is_owner ||
    production?.role === 'producer' ||
    production?.role === 'executive_producer' ||
    production?.role === 'director'

  const gate = useMemo(
    () => resolveCollaborationGate(production, members, eligibility, user?.id),
    [production, members, eligibility, user?.id],
  )

  useEffect(() => {
    void (async () => {
      try {
        const [prod, memRes, deptRes, elig] = await Promise.all([
          getProduction(productionId).catch(() => null),
          listMembers(productionId).catch(() => ({ items: [] as ProductionMember[] })),
          listDepartments(productionId).catch(() => ({ items: [] as ProductionDepartment[] })),
          getCollaborationEligibility(productionId).catch(() => null),
        ])
        setProduction(prod)
        setMembers(memRes.items)
        setDepartments(deptRes.items)
        setEligibility(elig)
      } catch {
        /* optional collaboration context */
      }
    })()
  }, [productionId])

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [scriptRes, overview] = await Promise.all([
        listScriptDocuments(productionId, { limit: 50, offset: 0 }),
        getProductionOverview(productionId).catch(() => null),
      ])
      setScripts(scriptRes.items)
      const sid = scriptId > 0 && scriptRes.items.some((d) => d.id === scriptId)
        ? scriptId
        : (scriptRes.items[0]?.id ?? 0)
      setScriptId(sid)

      const progressMap: Record<number, OverviewEpisodeProgress> = {}
      for (const p of overview?.progress ?? []) {
        progressMap[p.episode_no] = p
      }
      setProgressByEp(progressMap)

      if (sid > 0) {
        const epRes = await listScriptEpisodes(productionId, sid)
        setEpisodes(epRes.items)
        setEpisodeTarget(epRes.episode_target)
        setEpisodesPerSeason(epRes.episodes_per_season)
      } else {
        setEpisodes([])
      }
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'shots.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [productionId, scriptId])

  useEffect(() => {
    void reload()
  }, [productionId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!scriptId) return
    let cancelled = false
    ;(async () => {
      try {
        const epRes = await listScriptEpisodes(productionId, scriptId)
        if (cancelled) return
        setEpisodes(epRes.items)
        setEpisodeTarget(epRes.episode_target)
        setEpisodesPerSeason(epRes.episodes_per_season)
      } catch (e) {
        if (!cancelled) setError(resolveApiErrorMessage(e, 'shots.loadFailed'))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [productionId, scriptId])

  const seasonGroups = useMemo(
    () => groupEpisodesBySeason(episodes, episodesPerSeason, episodeTarget),
    [episodes, episodesPerSeason, episodeTarget],
  )

  const showSeasonTabs = usesSeasonSplit(episodesPerSeason, episodeTarget) && seasonGroups.length > 1
  const activeEpisodes = showSeasonTabs
    ? (seasonGroups.find((g) => g.seasonNo === Number(seasonTab))?.items ?? [])
    : episodes

  const { page, setPage, total, items: pageEpisodes } = useClientPagination(activeEpisodes, EPISODE_PAGE_SIZE)

  useEffect(() => {
    setPage(1)
  }, [seasonTab, scriptId, setPage])

  const scriptOptions = scripts.map((doc, i) => ({
    value: String(doc.id),
    label: doc.title?.trim() || t('script.unnamedScript', { n: i + 1 }),
  }))

  function toggleEpisodeExpanded(episodeNo: number) {
    setExpandedEpisodes((prev) => {
      const next = new Set(prev)
      if (next.has(episodeNo)) next.delete(episodeNo)
      else next.add(episodeNo)
      return next
    })
  }

  function openPreview(urls: string[], index: number, title: string) {
    setPreviewUrls(urls)
    setPreviewIndex(index)
    setPreviewTitle(title)
  }

  async function openEpisodeAssign(ep: ScriptEpisodeSummary) {
    if (!scriptId) return
    const label = formatEpisodeLabel(ep.episode_no, episodesPerSeason, episodeTarget)
    let sceneNos: number[] = []
    if (ep.scene_count > 0) {
      sceneNos = Array.from({ length: ep.scene_count }, (_, i) => i + 1)
    } else {
      try {
        const shotRes = await listShots(productionId, {
          script_id: scriptId,
          episode_no: ep.episode_no,
          limit: 500,
        })
        sceneNos = [...new Set(shotRes.items.map((s) => s.scene_no))].sort((a, b) => a - b)
      } catch (e) {
        setError(resolveApiErrorMessage(e, 'shots.loadFailed'))
        return
      }
    }
    if (sceneNos.length === 0) {
      setError(t('shots.assignEpisodeNoScenes'))
      return
    }
    setAssignPreset({
      expectedKind: 'storyboard',
      title: `${t('shots.assignWork')} · ${label}`,
      items: sceneNos.map((sceneNo) => ({
        item_type: 'shot_scene',
        item_public_id: buildShotSceneScopeKey(scriptId, ep.episode_no, sceneNo),
        label: `${label} · S${String(sceneNo).padStart(2, '0')}`,
      })),
    })
    setAssignOpen(true)
  }

  return (
    <CatalogListPageShell>
      <PageHeading icon={ListVideo} tone="indigo" title={t('shots.title')} subtitle={t('shots.subtitleV3')}>
        <div className="flex w-full flex-wrap items-center justify-end gap-2 lg:w-auto">
          <div className="min-w-[200px] max-w-xs flex-1">
            <ProtoDropdown
              value={String(scriptId || '')}
              onValueChange={(v) => setScriptId(Number(v))}
              options={scriptOptions}
              disabled={scripts.length === 0}
              placeholder={t('shots.fieldScript')}
              aria-label={t('shots.fieldScript')}
              compact
            />
          </div>
          <ProtoBtn size="sm" variant="secondary" onClick={() => void downloadShotsCsv(productionId)}>
            <Download className="h-3.5 w-3.5" />
            {t('shots.exportCsv')}
          </ProtoBtn>
          <CatalogListPrimaryButton disabled={!scriptId} onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('shots.add')}
          </CatalogListPrimaryButton>
        </div>
      </PageHeading>

      {showSeasonTabs ? (
        <ProtoTabs
          tabs={seasonGroups.map((g) => ({
            id: String(g.seasonNo),
            label: t('shots.seasonTab', { n: g.seasonNo }),
          }))}
          active={seasonTab}
          onChange={setSeasonTab}
        />
      ) : null}

      {error ? <p className="mb-4 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-indigo-400" />
        </div>
      ) : scripts.length === 0 ? (
        <PageEmptyState icon={ListVideo} tone="indigo" title={t('shots.noScriptTitle')} subtitle={t('shots.noScript')}>
          <Link to={`${base}/script`} className="text-sm text-indigo-300 hover:text-indigo-200">
            {t('shots.goScript')}
          </Link>
        </PageEmptyState>
      ) : activeEpisodes.length === 0 ? (
        <PageEmptyState icon={ListVideo} tone="indigo" title={t('shots.noEpisodeTitle')} subtitle={t('shots.noEpisode')}>
          <Link to={`${base}/script/${scriptId}/episodes/1`} className="text-sm text-indigo-300 hover:text-indigo-200">
            {t('shots.goScriptEditor')}
          </Link>
        </PageEmptyState>
      ) : (
        <>
          <ProtoListTable className="mt-4" production>
            <table className="min-w-full text-left text-sm">
              <thead className={cn(PRODUCTION_TABLE_HEAD_CLASS, 'text-xs text-zinc-500')}>
                <tr>
                  <th className="px-4 py-3">{t('shots.col.episode')}</th>
                  <th className="px-4 py-3">{t('shots.col.episodeTitle')}</th>
                  <th className="px-4 py-3">{t('shots.col.sceneCount')}</th>
                  <th className="px-4 py-3">{t('shots.col.shotCount')}</th>
                  <th className="px-4 py-3">{t('shots.col.progress')}</th>
                  <th className="px-4 py-3">{t('shots.col.action')}</th>
                </tr>
              </thead>
              <tbody>
                {pageEpisodes.map((ep) => {
                  const label = formatEpisodeLabel(ep.episode_no, episodesPerSeason, episodeTarget)
                  const prog = progressByEp[ep.episode_no]
                  const shotCount = prog?.total ?? 0
                  const approved = prog?.approved ?? 0
                  const expanded = expandedEpisodes.has(ep.episode_no)
                  return (
                    <Fragment key={ep.episode_no}>
                      <tr className={cn(PRODUCTION_TABLE_ROW_CLASS, 'bg-white/[0.02] hover:bg-white/[0.03]')}>
                        <td className="px-4 py-3">
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-auto gap-2 p-0 text-left font-mono text-xs text-indigo-200 hover:bg-transparent hover:text-indigo-100"
                            onClick={() => toggleEpisodeExpanded(ep.episode_no)}
                            aria-expanded={expanded}
                            aria-label={
                              expanded
                                ? t('shots.collapseEpisodeScenes', { label })
                                : t('shots.expandEpisodeScenes', { label })
                            }
                          >
                            {expanded ? (
                              <ChevronDown className="h-4 w-4 shrink-0 text-zinc-500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 shrink-0 text-zinc-500" />
                            )}
                            {label}
                          </Button>
                        </td>
                        <td className="px-4 py-3 text-zinc-300">{ep.title?.trim() || '—'}</td>
                        <td className="px-4 py-3 text-zinc-400">{ep.scene_count}</td>
                        <td className="px-4 py-3 text-zinc-400">{shotCount}</td>
                        <td className="px-4 py-3 text-xs text-zinc-500">
                          {shotCount > 0
                            ? t('shots.progressApproved', { approved, total: shotCount })
                            : t('shots.progressEmpty')}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-3">
                            <Link
                              to={`${base}/shots/${scriptId}/episodes/${ep.episode_no}`}
                              className="inline-flex items-center gap-1 text-xs text-indigo-300 hover:text-indigo-200"
                            >
                              <Clapperboard className="h-3.5 w-3.5" />
                              {t('shots.openEpisodeWorkbench')}
                            </Link>
                            {gate.canAssignWork ? (
                              <Button
                                type="button"
                                variant="link"
                                size="sm"
                                onClick={() => void openEpisodeAssign(ep)}
                                className="inline-flex h-auto items-center gap-1 p-0 text-xs text-sky-300 hover:text-sky-200"
                              >
                                <KanbanSquare className="h-3.5 w-3.5" />
                                {t('shots.assignWork')}
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {expanded && scriptId > 0 ? (
                        <EpisodeScenesExpandRows
                          productionId={productionId}
                          scriptId={scriptId}
                          episodeNo={ep.episode_no}
                          canEdit={canManageScenes}
                          onPreview={openPreview}
                        />
                      ) : null}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </ProtoListTable>
          <ProtoPagination
            page={page}
            pageSize={EPISODE_PAGE_SIZE}
            total={total}
            onPageChange={setPage}
            prevLabel={t('pagination.prev')}
            nextLabel={t('pagination.next')}
          />
        </>
      )}

      {createOpen ? (
        <ShotCreateModal
          productionId={productionId}
          defaultScriptId={scriptId}
          onClose={() => setCreateOpen(false)}
          onCreated={() => void reload()}
        />
      ) : null}

      <ImagePreviewModal
        open={previewUrls.length > 0}
        urls={previewUrls}
        index={previewIndex}
        title={previewTitle}
        onClose={() => setPreviewUrls([])}
        onIndexChange={setPreviewIndex}
      />

      <AssignWorkItemModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        productionId={productionId}
        members={members}
        departments={departments}
        preset={assignPreset}
        gate={gate}
      />
    </CatalogListPageShell>
  )
}
